import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToasterService, TranslateService } from '@devinforius/i-essentials';
import { INavbarConfig } from '@devinforius/i-essentials/lib/interfaces';
import { OAuthService } from 'angular-oauth2-oidc';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';

import { AuthService } from './core/services/authentication.service';
import { CustomerService } from './core/services/customer.service';
import { Language } from '@shared/types';

/**
 * Composant racine de l'application
 * Gère l'authentification, la traduction, la navigation et la configuration globale
 */
@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  // Injection des services nécessaires
  private readonly authService: AuthService = inject(AuthService);
  private readonly oAuthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);
  private readonly translater: TranslateService = inject(TranslateService);
  private readonly toaster: ToasterService = inject(ToasterService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly customerService: CustomerService = inject(CustomerService);

  private isTranslationReady: boolean = false;
  private isUserReady: boolean = false;
  private customerSubscription: Subscription | undefined;

  public loading: boolean = true;
  public isConnected: boolean = false;

  /**
   * Surveille la connexion de l'utilisateur
   * Se déclenche automatiquement quand l'utilisateur se connecte ou se déconnecte
   */
  public isConnectedEff = effect(() => {
    if (
      this.authService.isConnected() ||
      this.oAuthService.hasValidAccessToken()
    ) {
      this.isUserReady = true;
      this.setINavBarConfig();
      this.loading = false;
      this.isConnected = true;

      // Redirection vers l'accueil si l'utilisateur arrive sur la page callback
      if (this.router.url.startsWith('/callback')) {
        this.router.navigate(['home']);
      }
    }
  });

  /** Configuration de la barre de navigation */
  public config!: INavbarConfig;
  /**
   * Initialisation du composant
   * Configure les traductions et les données client
   */
  ngOnInit(): void {
    (async () => {
      await this.initTranslation();

      this.translater.onTranslationReady.subscribe((value: boolean) => {
        if (value) {
          this.reloadApp();
          this.isTranslationReady = true;
          // S'assurer que l'attribut lang est correctement défini
          const currentLang = localStorage.getItem('lang') as Language;
          this.updateHtmlLangAttribute(currentLang);
          this.setINavBarConfig();
        }
      });
      this.applyCustomerDataInConfig();
    })();
  }

  /**
   * Applique les données client dans la configuration de la navbar
   * Gère les cas où les données sont déjà en cache ou doivent être récupérées
   */
  private applyCustomerDataInConfig(): void {
    if (this.customerService.hasCustomer()) {
      // Récupérer les données du client dans sessionStorage
      this.customerService.getCustomer().subscribe((customer) => {
        // Mise à jour de config
        this.config = {
          ...this.config,
          names: {
            ...this.config.names,
            home: customer?.name ?? 'app',
          },
        };
      });
    } else {
      // Souscription à l'observable customer$ pour récupérer les données du client
      this.customerSubscription = this.customerService.customer$.subscribe(
        (customer) => {
          // Mise à jour de config
          this.config = {
            ...this.config,
            names: {
              ...this.config.names,
              home: customer?.name ?? 'app',
            },
          };
        }
      );
    }
  }

  /**
   * Nettoyage lorsque qu'on quitte composant
   * Arrete les effects et se désabonne des observables pour éviter les fuites mémoire
   */
  public ngOnDestroy(): void {
    this.isConnectedEff.destroy();
    this.customerSubscription?.unsubscribe();
  }

  /**
   * Déconnecte l'utilisateur de l'application
   */
  public logout(): void {
    this.oAuthService.logOut();
  }

  /**
   * Configure la barre de navigation avec les liens et menus
   * N'exécute la configuration que si les traductions et utilisateur sont prêts
   */
  private setINavBarConfig(): void {
    if (!this.isTranslationReady || !this.isUserReady) {
      return;
    }

    this.config = {
      names: { app: 'Idepa', home: 'app', user: '' },
      links: {
        navLinks: [
          {
            icon: 'bi-table',
            title: this.translater.translate('menu.statements'),
            count: undefined,
            click: () => this.router.navigate(['home']),
          },
        ],
      },
      routes: { assistance: '/assistance' },
      menus: {
        userMenuItems: [],
        langMenuItems: [
          {
            icon: '../assets/flags/fr.svg',
            label: 'Français',
            command: () => {
              this.updateLanguage('fr');
            },
          },
          {
            icon: '../assets/flags/nl.svg',
            label: 'Nederlands',
            command: () => {
              this.updateLanguage('nl');
            },
          },
          {
            icon: '../assets/flags/en.svg',
            label: 'English',
            command: () => {
              this.updateLanguage('en');
            },
          },
          {
            icon: '../assets/flags/de.svg',
            label: 'Deutsch',
            command: () => {
              this.updateLanguage('de');
            },
          },
        ],
      },
      isSmallSidebar: true,
      appColor: '#FFF',
      showButtons: { admin: true, languages: true },
    };
  }

  /**
   * Met à jour la langue de l'application
   * @param lang Nouvelle langue à appliquer
   */
  private async updateLanguage(lang: Language): Promise<void> {
    this.loading = true;
    localStorage.setItem('lang', lang);
    await this.translater.setCurrentLang(lang);
    // Mettre à jour l'attribut lang du HTML
    this.updateHtmlLangAttribute(lang);
    this.reloadApp();
  }

  /**
   * Initialise le système de traduction
   * Détecte la langue du navigateur si aucune langue n'est définie
   * Configure le service de traduction avec les langues supportées
   */
  private async initTranslation(): Promise<void> {
    this.loading = true;

    const lang: string | null = localStorage.getItem('lang');
    const allLanguages: Language[] = ['en', 'nl', 'de', 'fr'];

    if (!lang || lang === 'undefined' || lang === 'null') {
      const userLang: string = navigator.language
        .split('-')
        .reverse()[0]
        .toLowerCase();
      const defaultLang =
        allLanguages.indexOf(userLang as Language) >= 0 ? userLang : 'fr';
      localStorage.setItem('lang', defaultLang);
    }

    // Récupérer la langue actuelle pour l'utiliser
    const currentLang = localStorage.getItem('lang') as Language;

    // Mettre à jour l'attribut lang du HTML avec la langue actuelle
    this.updateHtmlLangAttribute(currentLang);

    await this.translater.init(currentLang, allLanguages, [
      'assets/translations',
      'assets/i18n',
    ]);

    this.toaster.setKey('MyApp');
    this.reloadApp();
  }

  /**
   * Recharge l'affichage de l'application
   * Désactive le loader pour afficher le contenu principal
   */
  private reloadApp(): void {
    // on met le loading à false pour afficher le contenu de l'application
    this.loading = false;
  }

  /**
   * Met à jour l'attribut lang de la balise HTML pour l'accessibilité
   * @param lang Code de la langue à appliquer
   */
  private updateHtmlLangAttribute(lang: string): void {
    this.document.documentElement.lang = lang;
    console.log(`Langue HTML mise à jour: ${lang}`);
  }
}
